/**
 * ReminderAlarm — full-screen in-app ALARM for Buddy reminders.
 * When a "buddy_reminder" arrives (app open), instead of a quiet toast the
 * user gets a real alarm: looping alarm sound + continuous vibration + a
 * prominent overlay with a close button. Self-contained: listens to the
 * global "osc-user-notify" window event dispatched by Shell's websocket.
 */
import React, { useEffect, useRef, useState } from "react";
import { AlarmClock } from "lucide-react";
import { Button } from "./ui/button";
import { useApp } from "../context/AppContext";
import { startAlarmSound } from "../lib/sounds";
import { startAlarmVibration } from "../lib/haptics";

export const ReminderAlarm = () => {
  const { t } = useApp();
  const [items, setItems] = useState([]); // reminder texts (can stack)
  const stopSoundRef = useRef(null);
  const stopVibeRef = useRef(null);
  const safetyRef = useRef(null);

  useEffect(() => {
    const onNotify = (e) => {
      const p = e.detail || {};
      if (p.inner === "buddy_reminder" && p.text) {
        setItems((prev) => (prev.includes(p.text) ? prev : [...prev, p.text]));
      }
    };
    window.addEventListener("osc-user-notify", onNotify);
    // cold start via notification tap: the push handler stores the reminder
    // before this component exists — ring it now (fresh ones only, < 3 min)
    try {
      const raw = localStorage.getItem("osc_ring_reminder");
      if (raw) {
        localStorage.removeItem("osc_ring_reminder");
        const { text, ts } = JSON.parse(raw);
        if (text && Date.now() - (ts || 0) < 180000) {
          setItems((prev) => (prev.includes(text) ? prev : [...prev, text]));
        }
      }
    } catch (e) { /* ignore */ }
    return () => window.removeEventListener("osc-user-notify", onNotify);
  }, []);

  const active = items.length > 0;

  const stopNoise = () => {
    try { stopSoundRef.current?.(); } catch (e) { /* ignore */ }
    stopSoundRef.current = null;
    try { stopVibeRef.current?.(); } catch (e) { /* ignore */ }
    stopVibeRef.current = null;
    clearTimeout(safetyRef.current);
  };

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    // CRITICAL (iOS): tell BuddyAssistant to SUSPEND the wake-word recognizer
    // while the alarm rings. Stopping the alarm audio reconfigures the iOS
    // audio session — doing that under a running SFSpeechRecognizer crashes
    // the whole app (same class as the mic-tap crash).
    try { window.dispatchEvent(new CustomEvent("osc-alarm-ringing", { detail: { ringing: true } })); } catch (e) { /* ignore */ }
    (async () => {
      const stop = await startAlarmSound();
      if (cancelled) { try { stop?.(); } catch (e) { /* ignore */ } return; }
      stopSoundRef.current = stop;
    })();
    stopVibeRef.current = startAlarmVibration();
    // safety: stop ringing/buzzing after 90s — the overlay stays until closed
    safetyRef.current = setTimeout(stopNoise, 90000);
    return () => {
      cancelled = true;
      stopNoise();
      // resume the wake listener only AFTER the audio session settles
      setTimeout(() => {
        try { window.dispatchEvent(new CustomEvent("osc-alarm-ringing", { detail: { ringing: false } })); } catch (e) { /* ignore */ }
      }, 400);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const close = () => {
    stopNoise();
    setItems([]);
  };

  if (!active) return null;

  return (
    <div
      data-testid="reminder-alarm-overlay"
      className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-black/60 backdrop-blur-md"
      role="alertdialog"
      aria-label={t("reminder_alarm_title")}
    >
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card text-card-foreground shadow-2xl p-6 flex flex-col items-center text-center gap-4">
        <div className="relative flex items-center justify-center">
          <span className="absolute inline-flex h-20 w-20 rounded-full bg-destructive/30 animate-ping" />
          <span className="relative flex items-center justify-center h-20 w-20 rounded-full bg-destructive text-destructive-foreground">
            <AlarmClock className="h-10 w-10" />
          </span>
        </div>
        <div>
          <h2 className="text-xl font-bold" data-testid="reminder-alarm-title">{t("reminder_alarm_title")}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{t("reminder_alarm_sub")}</p>
        </div>
        <div className="w-full space-y-2" data-testid="reminder-alarm-texts">
          {items.map((txt, i) => (
            <div key={i} className="rounded-lg bg-muted px-4 py-3 text-base font-medium">
              {txt}
            </div>
          ))}
        </div>
        <Button
          data-testid="reminder-alarm-close-btn"
          className="w-full h-12 text-base"
          onClick={close}
        >
          {t("reminder_alarm_close")}
        </Button>
      </div>
    </div>
  );
};
